import { FC } from 'react';
import classNames from 'classnames';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  OpmFirstLastWaterfallDictionaryDto,
  FirstLastTranche,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import styles from '../../opm-summary-waterfall-erf.module.scss';

interface TrancheMoMRowProps {
  valueKey: keyof NonNullable<FirstLastTranche['conditions'][number]['momHurdleDetails']>;
  label: string;
  trancheIndex: number;
  bold?: boolean;
  conditionIndex?: number;
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  selectedInstrument: string;
  waterfallIterations: number;
  getTrancheDictionary: (firstOrLastFive: 'first' | 'last') => Dictionary<FirstLastTranche>[];
}

const TrancheMoMRow: FC<TrancheMoMRowProps> = ({
  valueKey,
  label,
  trancheIndex,
  bold = false,
  conditionIndex,
  opmWaterfall,
  selectedYear,
  selectedInstrument,
  waterfallIterations,
  getTrancheDictionary,
}) => {
  const tranches = opmWaterfall[selectedYear].first[1].instruments[selectedInstrument].tranches;
  const trancheNames = Object.keys(tranches);
  const tranche = tranches[trancheNames[trancheIndex]];

  const condition =
    typeof conditionIndex === 'number'
      ? tranche.conditions[conditionIndex]
      : tranche.conditions.find((c) => c.metric === 'InstitutionalMoM');

  if (!condition || !condition.momHurdleDetails) {
    return null;
  }

  const getMomHurdleDetail = (
    dict: Dictionary<FirstLastTranche>,
    trancheIndex: number,
    conditionIndex?: number
  ) => {
    const thisTrancheName = Object.keys(dict)[trancheIndex];
    const thisTranche = dict[thisTrancheName];
    const cond =
      typeof conditionIndex === 'number'
        ? thisTranche.conditions[conditionIndex]
        : thisTranche.conditions.find((c) => c.momHurdleDetails);
    return cond?.momHurdleDetails;
  };

  return (
    <tr key={`mom-row-${valueKey}-${trancheIndex}-${conditionIndex}`}>
      <td className={classNames(styles['border-right'], bold && styles['bold-text'])}>{label}</td>
      {[...getTrancheDictionary('first'), ...getTrancheDictionary('last')].map((dict, index) => {
        const momDetails = getMomHurdleDetail(dict, trancheIndex, conditionIndex);
        const value = momDetails?.[valueKey] ?? null;
        return (
          <td
            key={`mom-row-value-${valueKey}-${trancheIndex}-${conditionIndex}-${index}`}
            className={classNames({
              [styles['border-right']]: index + 1 === waterfallIterations,
            })}
            data-testid={`momRow-${valueKey}-tranche${
              trancheIndex + 1
            }-condition${conditionIndex}-simulation-${index}`}>
            <CellValue value={value} {...amountRules} />
          </td>
        );
      })}
    </tr>
  );
};

export default TrancheMoMRow;
